import { BaseService } from './common/baseService';
import { supabase } from "@/integrations/supabase/client";
import type { Client } from './common/types';

export interface CreateClientData {
  name: string;
  country?: string;
  industry?: string;
  status?: string;
  funnel_stage?: string;
  logo_url?: string;
  payment_terms?: string;
}

export interface UpdateClientData extends Partial<CreateClientData> {
  id: string;
}

class ClientsService extends BaseService {
  constructor() {
    super('clients');
  }

  async getClients(): Promise<Client[]> {
    return this.queryTable(() => 
      supabase.from('clients').select('*').order('updated_at', { ascending: false })
    );
  }

  async getClientById(id: string): Promise<Client | null> {
    return this.queryTable(() => 
      supabase.from('clients').select('*').eq('id', id).maybeSingle()
    );
  }

  async createClient(clientData: CreateClientData): Promise<Client> {
    return this.queryTable(() => 
      supabase.from('clients').insert([clientData]).select().single()
    );
  }

  async updateClient({ id, ...updateData }: UpdateClientData): Promise<Client> {
    return this.queryTable(() => 
      supabase.from('clients').update(updateData).eq('id', id).select().single()
    );
  }

  async deleteClient(id: string): Promise<void> {
    return this.queryTable(() => 
      supabase.from('clients').delete().eq('id', id)
    );
  }

  async getClientsWithStats() {
    return this.queryTable(() => 
      supabase.from('clients').select(`
        *,
        proposals:proposals(count),
        projects:projects(count),
        invoices:invoices(count)
      `)
    );
  }

  async searchClients(searchTerm: string): Promise<Client[]> {
    return this.queryTable(() => 
      supabase.from('clients').select('*')
        .or(`name.ilike.%${searchTerm}%,country.ilike.%${searchTerm}%,industry.ilike.%${searchTerm}%`)
        .order('updated_at', { ascending: false })
    );
  }

  async getClientsByStatus(status: string): Promise<Client[]> {
    return this.queryTable(() => 
      supabase.from('clients').select('*').eq('status', status).order('name', { ascending: true })
    );
  }

  async getClientsByFunnelStage(funnelStage: string): Promise<Client[]> {
    return this.queryTable(() => 
      supabase.from('clients').select('*').eq('funnel_stage', funnelStage).order('name', { ascending: true })
    );
  }
}

export const clientsService = new ClientsService();